(function () {
  'use strict';

  if (window.EventRepresentativesPage) {
    window.EventRepresentativesPage.init(document);
    return;
  }

  var roots = new WeakMap();
  var rootSelector = '[data-event-representatives-page]';
  var easeOutQuart = 'cubic-bezier(0.25, 1, 0.5, 1)';

  function initialize(root) {
    if (!root || roots.has(root)) return;

    var controller = new AbortController();
    var signal = controller.signal;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var faqAnimations = new WeakMap();
    var scrollFrame = 0;
    var headerTicking = false;

    roots.set(root, controller);

    var year = root.querySelector('#year');
    if (year) year.textContent = String(new Date().getFullYear());

    function canAnimate() {
      return !reducedMotion.matches && typeof Element.prototype.animate === 'function';
    }

    function playQuietEntrance() {
      if (root.dataset.enableEntranceAnimation === 'false' || !canAnimate()) return;

      var header = root.querySelector('.header-inner');
      var heroCopy = root.querySelector('.hero-copy');
      var bookingCard = root.querySelector('.hero-booking');

      if (header) {
        header.animate(
          [
            { opacity: 0.9, transform: 'translateY(-4px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 520, easing: easeOutQuart }
        );
      }

      if (heroCopy) {
        heroCopy.animate(
          [
            { opacity: 0.72, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 720, easing: easeOutQuart }
        );
      }

      if (bookingCard) {
        bookingCard.animate(
          [
            { opacity: 0.78, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { delay: 80, duration: 760, easing: easeOutQuart }
        );
      }
    }

    function clearFaqStyles(item) {
      item.classList.remove('is-animating');
      item.style.removeProperty('height');
      item.style.removeProperty('overflow');
    }

    function cancelFaqAnimation(item) {
      var animation = faqAnimations.get(item);
      if (animation) {
        faqAnimations.delete(item);
        animation.onfinish = null;
        animation.oncancel = null;
        animation.cancel();
      }
      clearFaqStyles(item);
    }

    function completeFaqAnimation(item, animation) {
      if (faqAnimations.get(item) !== animation) return;
      faqAnimations.delete(item);
      clearFaqStyles(item);
    }

    function closeFaq(item) {
      if (!item.open) return;
      cancelFaqAnimation(item);

      if (!canAnimate()) {
        item.open = false;
        return;
      }

      var startHeight = item.offsetHeight;
      var endHeight = item.querySelector('summary').offsetHeight;
      item.classList.add('is-animating');
      item.style.overflow = 'hidden';
      var animation = item.animate(
        { height: [startHeight + 'px', endHeight + 'px'] },
        { duration: 360, easing: easeOutQuart }
      );
      faqAnimations.set(item, animation);
      animation.onfinish = function () {
        item.open = false;
        completeFaqAnimation(item, animation);
      };
      animation.oncancel = function () {
        completeFaqAnimation(item, animation);
      };
    }

    function openFaq(item, faqItems) {
      if (item.open) return;
      faqItems.forEach(function (other) {
        if (other !== item) closeFaq(other);
      });

      cancelFaqAnimation(item);
      if (!canAnimate()) {
        item.open = true;
        return;
      }

      var summaryHeight = item.querySelector('summary').offsetHeight;
      item.open = true;
      var endHeight = item.offsetHeight;
      item.classList.add('is-animating');
      item.style.overflow = 'hidden';
      var animation = item.animate(
        { height: [summaryHeight + 'px', endHeight + 'px'] },
        { duration: 440, easing: easeOutQuart }
      );
      faqAnimations.set(item, animation);
      animation.onfinish = function () {
        completeFaqAnimation(item, animation);
      };
      animation.oncancel = function () {
        completeFaqAnimation(item, animation);
      };
    }

    var faqItems = Array.from(root.querySelectorAll('.faq-list details'));
    faqItems.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;
      summary.addEventListener(
        'click',
        function (event) {
          event.preventDefault();
          if (item.open) {
            closeFaq(item);
          } else {
            openFaq(item, faqItems);
          }
        },
        { signal: signal }
      );
    });

    function cancelSmoothScroll() {
      if (!scrollFrame) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
    }

    function smoothScrollTo(targetY, onComplete) {
      cancelSmoothScroll();
      var startY = window.scrollY;
      var distance = targetY - startY;

      if (reducedMotion.matches || Math.abs(distance) < 2) {
        window.scrollTo(0, targetY);
        if (onComplete) onComplete();
        return;
      }

      var duration = Math.min(1000, Math.max(700, Math.abs(distance) * 0.36));
      var startedAt = window.performance.now();

      function step(now) {
        var progress = Math.min(1, (now - startedAt) / duration);
        var eased = progress < 0.5
          ? 4 * Math.pow(progress, 3)
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        window.scrollTo(0, startY + distance * eased);

        if (progress < 1) {
          scrollFrame = window.requestAnimationFrame(step);
        } else {
          scrollFrame = 0;
          if (onComplete) onComplete();
        }
      }

      scrollFrame = window.requestAnimationFrame(step);
    }

    root.querySelectorAll('a[href^="#"]:not(.skip-link)').forEach(function (link) {
      link.addEventListener(
        'click',
        function (event) {
          var id = link.getAttribute('href');
          var target = id && id !== '#' ? root.querySelector(id) : null;
          if (!target) return;

          event.preventDefault();
          var header = root.querySelector('.site-header');
          var headerHeight = header ? header.offsetHeight : 0;
          var targetY = id === '#top'
            ? root.getBoundingClientRect().top + window.scrollY
            : Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - 18);

          smoothScrollTo(targetY, function () {
            window.history.replaceState(null, '', id);
          });
        },
        { signal: signal }
      );
    });

    function updateHeaderState() {
      root.classList.toggle('is-scrolled', window.scrollY > root.offsetTop + 24);
      headerTicking = false;
    }

    window.addEventListener('wheel', cancelSmoothScroll, { passive: true, signal: signal });
    window.addEventListener('touchstart', cancelSmoothScroll, { passive: true, signal: signal });
    window.addEventListener(
      'keydown',
      function (event) {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
          cancelSmoothScroll();
        }
      },
      { signal: signal }
    );
    window.addEventListener(
      'scroll',
      function () {
        if (headerTicking) return;
        headerTicking = true;
        window.requestAnimationFrame(updateHeaderState);
      },
      { passive: true, signal: signal }
    );

    updateHeaderState();
    window.requestAnimationFrame(playQuietEntrance);
  }

  function initializeWithin(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(rootSelector)) initialize(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(rootSelector).forEach(initialize);
    }
  }

  function destroyWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var candidates = [];
    if (scope.matches && scope.matches(rootSelector)) candidates.push(scope);
    scope.querySelectorAll(rootSelector).forEach(function (root) {
      candidates.push(root);
    });
    candidates.forEach(function (root) {
      var controller = roots.get(root);
      if (controller) controller.abort();
      roots.delete(root);
    });
  }

  window.EventRepresentativesPage = {
    init: initializeWithin,
    destroy: destroyWithin,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeWithin(document);
    }, { once: true });
  } else {
    initializeWithin(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeWithin(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });
})();
